import React from 'react';
import { Box } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';

// Shared framer-motion segmented control for the new design language. Pill track with an animated
// sliding active indicator (spring, reduced-motion-safe). Active = white text on primary (#DC5829),
// inactive = #6B625B. Reproduced byte-for-byte from the former per-page render helpers.
//
// `layoutId` is REQUIRED and must be UNIQUE per instance: framer-motion groups layout animations by
// id, so two segmented filters sharing one id would make the active indicator "fly" between them.
// Each call site passes its own literal (e.g. 'inventory-status-active', 'draft-status-active').
const SegmentedFilter: React.FC<{
    options: Array<{ value: string; label: string }>;
    value: string;
    onChange: (value: string) => void;
    layoutId: string;
    ariaLabel?: string;
}> = ({ options, value, onChange, layoutId, ariaLabel = 'Filter' }) => {
    const reduceMotion = useReducedMotion();

    return (
        <Box
            role="radiogroup"
            aria-label={ariaLabel}
            sx={{
                display: 'inline-flex', p: '3px', borderRadius: '999px', bgcolor: '#f4f1ea',
                border: '1px solid #e7e1d4', flexShrink: 0,
                // On narrow screens the pill track can exceed the viewport (e.g. 4 options on ~320px);
                // scroll horizontally to keep the pill shape intact rather than wrapping/breaking it.
                maxWidth: '100%', overflowX: 'auto',
            }}
        >
            {options.map(opt => {
                const active = value === opt.value;
                return (
                    <Box
                        key={opt.value}
                        component="button"
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => onChange(opt.value)}
                        sx={{
                            position: 'relative', cursor: 'pointer', userSelect: 'none',
                            px: 1.6, py: 0.5, borderRadius: '999px', border: 'none', background: 'transparent',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0, whiteSpace: 'nowrap',
                            fontFamily: 'inherit', fontSize: '0.78rem', fontWeight: 700,
                            color: active ? '#fff' : '#6B625B',
                            transition: 'color 160ms',
                            '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: '1px' },
                        }}
                    >
                        {active && (
                            <motion.span
                                layout={!reduceMotion}
                                layoutId={reduceMotion ? undefined : layoutId}
                                transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 34 }}
                                style={{
                                    position: 'absolute', inset: 0, borderRadius: '999px',
                                    background: '#DC5829', zIndex: 0,
                                }}
                            />
                        )}
                        <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>{opt.label}</Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default SegmentedFilter;
